import { Debug, SymbolType } from "@spawn/common";
import { service } from "@spawn/core";
import { LaglessGenerator, LaglessspinConfigName } from "@spawn/slot-machine";

@service
export class NrgLaglessGenerator extends LaglessGenerator {
	/* eslint-disable @typescript-eslint/no-unused-vars */
	public override getLaglessSymbols(amount: number, reelIndex: number, configName?: string): SymbolType[];
	public override getLaglessSymbols(
		amount: number,
		reelIndex: number,
		configName?: LaglessspinConfigName,
		adjacentSymbol?: SymbolType
	): SymbolType[];
	public override getLaglessSymbols(
		amount: number,
		reelIndex: number,
		configName?: LaglessspinConfigName,
		adjacentSymbol?: SymbolType
	): SymbolType[] {
		if (!amount) {
			return [];
		}

		return amount === 1
			? this.getRandomNonCriticalSymbols(1) // ensures we don't accidentally get a single mystery symbol
			: super.getLaglessSymbols(amount, reelIndex, configName, adjacentSymbol);
	}

	/**
	 * This is a deterministic reelstrip generator that uses a string as a seed.
	 * It is used to generate a reelstrip with a specific count of symbols.
	 * @param count - The number of symbols to generate.
	 * @param reelIndex - The index of the reel to generate the reelstrip for.
	 * @param seed - The seed to use for the random number generator.
	 * @returns A reelstrip with a specific count of symbols.
	 */
	public getDeterministicReelstrip(count: number, reelIndex: number, seed: string): SymbolType[] {
		Debug.assertNumberInRange(reelIndex, 0, 4, "Reel index must be between 0 and 4");

		const nonCriticalSymbols = this.laglessSpinConfigs()
			[this.activeLaglessspinConfig].symbols.get(reelIndex)!
			.filter((symbol) => !this.laglessSpinConfigs()[this.activeLaglessspinConfig].criticalSymbols.has(symbol));

		const startingIndex = Math.floor(this.deterministicRandom(seed) * (nonCriticalSymbols.length - count));
		return nonCriticalSymbols.slice(startingIndex, startingIndex + count);
	}

	/**
	 * This is a deterministic random number generator that uses a string as a seed.
	 * It is used to generate a random number between 0 and 1 like Math.random() but with a seed.
	 * @param seed - The seed to use for the random number generator.
	 * @returns A random number between 0 and 1.
	 */
	private deterministicRandom(seed: string): number {
		// Step 1: Convert string to numeric seed using simple hash
		let hash = 0;
		for (let i = 0; i < seed.length; i++) {
			const char = seed.codePointAt(i) ?? 0;
			hash = (hash << 5) - hash + char;
			hash = hash & hash; // Convert to 32-bit integer
		}

		// Step 2: Ensure positive seed value
		let numericSeed = Math.abs(hash);
		if (numericSeed === 0) {
			numericSeed = 1;
		}

		// Step 3: Apply Linear Congruential Generator formula
		// Using Park and Miller's "minimal standard" LCG parameters
		const a = 16807; // multiplier
		const m = 2147483647; // modulus (2^31 - 1, a Mersenne prime)

		numericSeed = (numericSeed * a) % m;

		// Step 4: Convert to [0, 1) range
		return (numericSeed - 1) / (m - 1);
	}

	public getRandomNonCriticalSymbols(count: number): SymbolType[] {
		const nonCriticalSymbols = this.laglessSpinConfigs()
			[this.activeLaglessspinConfig].symbols.get(0)!
			.filter((symbol) => !this.laglessSpinConfigs()[this.activeLaglessspinConfig].criticalSymbols.has(symbol));
		const startingIndex = Math.floor(Math.random() * (nonCriticalSymbols.length - count));
		return nonCriticalSymbols.slice(startingIndex, startingIndex + count);
	}
}
